import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { getLeaves, applyLeave } from "../../../../services/StudentPortal/StudentLeaveService";
import { handleApiError } from "../../../../utils/errorHandler";
import { unwrapList } from "../../utils/studentPortalUtils";

/**
 * Data + actions for the student "Leaves" page: the applied/past leave list
 * plus the apply-leave mutation, kept together so the page component only
 * has to render state instead of juggling its own axios calls.
 *
 * Confirmed against the real backend (2026-10-22): student/leave (GET)
 * needs student_id, program_id AND session_id, and the POST (apply) must be
 * multipart/form-data, not JSON.
 */
export const useStudentLeave = (studentId, programId, sessionId) => {
  const [leaves, setLeaves] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchLeaves = useCallback(async () => {
    if (!studentId || !programId || !sessionId) return;
    setIsLoading(true);
    try {
      const response = await getLeaves({ student_id: studentId, program_id: programId, session_id: sessionId });
      setLeaves(unwrapList(response));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [studentId, programId, sessionId]);

  const submitLeave = useCallback(
    async ({ from_date, to_date, reason }) => {
      setIsSubmitting(true);
      try {
        // Confirmed against the real backend (2026-10-26): needs
        // program_id/session_id alongside student_id, and the reason field
        // is named `apply_reason`, not `reason`.
        const formData = new FormData();
        formData.append("student_id", studentId);
        formData.append("program_id", programId);
        formData.append("session_id", sessionId);
        formData.append("from_date", from_date);
        formData.append("to_date", to_date);
        formData.append("apply_reason", reason);
        await applyLeave(formData);
        enqueueSnackbar("Leave application submitted", { variant: "success" });
        await fetchLeaves();
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [studentId, programId, sessionId, fetchLeaves]
  );

  useEffect(() => {
    fetchLeaves();
  }, [fetchLeaves]);

  return { leaves, isLoading, isSubmitting, submitLeave, fetchLeaves };
};
